import { BaseRule } from "../../../../../types";
import {
  normalizeTargetingCondition,
  normalizeTargetingOption,
} from "../../../../hooks/common/targetingConditionNormalization";
import {
  replaceFirstNumber,
  replaceSecondNumber,
} from "./ruleValueHelpers";

type RuleCondition = string | null;
type RuleOption = string | null;

export const URL_HOST_VARIABLES = ["url", "host", "hash"];
export const KEY_VALUE_VARIABLES = [
  "query",
  "javascriptvariable",
  "cookie",
  "datalayervariable",
  "utm_campaign",
  "utm_source",
  "utm_medium",
  "utm_term",
  "utm_content",
];
export const UTM_VARIABLES = [
  "utm_campaign",
  "utm_source",
  "utm_medium",
  "utm_term",
  "utm_content",
];
export const OPTIONS_VARIABLES = ["device", "browser", "os", "country", "continent", "domain"];

export const getDefaultConditionAndOption = (
  variable: string
): { condition: RuleCondition; option: RuleOption } => {
  if (URL_HOST_VARIABLES.includes(variable)) {
    return {
      condition: "contains",
      option: "any_of",
    };
  }

  if (KEY_VALUE_VARIABLES.includes(variable)) {
    return {
      condition: "equals",
      option: "any_of",
    };
  }

  if (OPTIONS_VARIABLES.includes(variable) || variable === "new_vs_returning") {
    return {
      condition: "is",
      option: "any_of",
    };
  }

  if (variable === "time_since_first_visit") {
    return {
      condition: "is_greater_than",
      option: null,
    };
  }

  return {
    condition: null,
    option: null,
  };
};

export const normalizeRuleForEditor = <TRule extends BaseRule>(rule: TRule): TRule => {
  const defaults = getDefaultConditionAndOption(rule.variable);
  return {
    ...rule,
    condition: normalizeTargetingCondition(rule.condition) || defaults.condition,
    option: normalizeTargetingOption(rule.option) || defaults.option,
    values: { ...(rule.values || {}) },
  };
};

export const applyRuleChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  variable: string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    const nextDefaults = getDefaultConditionAndOption(variable);
    const valueKey = Object.keys(item.values || {})[0] || String(item.id);
    return {
      ...item,
      variable,
      condition: nextDefaults.condition,
      option: nextDefaults.option,
      values: URL_HOST_VARIABLES.includes(variable) ? { [valueKey]: "" } : {},
    };
  });
};

export const applyMatchTypeChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  condition: string
): BaseRule[] => {
  return items.map((item) =>
    item.id === ruleItemId ? { ...item, condition } : item
  );
};

export const applyMatchOptionChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  option: string
): BaseRule[] => {
  return items.map((item) =>
    item.id === ruleItemId ? { ...item, option } : item
  );
};

export const applyTimeOptionChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  suffix: string,
  generateShortKey: () => string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    let newValue = 0;
    const values = Object.values(item.values);
    if (values.length > 0) {
      const firstItem = values.find((value) => value);
      if (firstItem) {
        const match = firstItem.match(/\d+/);
        if (match) {
          newValue = parseInt(match[0], 10);
        }
      }
    }

    return {
      ...item,
      values: {
        [generateShortKey()]: `${newValue}${suffix}`,
      },
    };
  });
};

export const applyTimeValueChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  value: string,
  generateShortKey: () => string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    const oldValues = Object.values(item.values);
    const firstItem = oldValues.find((oldValue) => oldValue);
    const nextValue = firstItem
      ? `${value}${firstItem.replace(/[^a-zA-Z]/g, "")}`
      : value;

    return {
      ...item,
      values: {
        [generateShortKey()]: nextValue,
      },
    };
  });
};

export const applyFirstTimeValueChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  value: string,
  generateShortKey: () => string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    const oldValues = Object.values(item.values);
    const firstItem = oldValues.find((oldValue) => oldValue);
    const nextValue = firstItem
      ? replaceFirstNumber(firstItem, value || "")
      : value;

    return {
      ...item,
      values: {
        [generateShortKey()]: nextValue,
      },
    };
  });
};

export const applySecondTimeValueChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  value: string,
  generateShortKey: () => string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    const oldValues = Object.values(item.values);
    const firstItem = oldValues.find((oldValue) => oldValue);
    const nextValue = firstItem
      ? replaceSecondNumber(firstItem, value || "")
      : value;

    return {
      ...item,
      values: {
        [generateShortKey()]: nextValue,
      },
    };
  });
};

export const applyKVKeyChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  key: string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    const oldValues = Object.values(item.values);
    const firstValue = oldValues.find((oldValue) => oldValue);
    return {
      ...item,
      values: {
        [key]: firstValue || "",
      },
    };
  });
};

export const applyKVValueChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  value: string,
  generateShortKey: () => string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    const oldKeys = Object.keys(item.values);
    const firstKey = oldKeys.find((oldKey) => oldKey);
    return {
      ...item,
      values: firstKey
        ? { [firstKey]: value }
        : { [generateShortKey()]: value },
    };
  });
};

export const applyOptionsChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  selectedValues: string[],
  generateShortKey: () => string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }

    const newValues: { [key: string]: string } = {};
    selectedValues.forEach((value) => {
      newValues[generateShortKey()] = value;
    });

    return { ...item, values: newValues };
  });
};

export const applyAddValue = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  generateShortKey: () => string,
  value: string
): BaseRule[] => {
  return items.map((item) =>
    item.id === ruleItemId
      ? {
          ...item,
          values: {
            ...item.values,
            [generateShortKey()]: value,
          },
        }
      : item
  );
};

export const applyValueChange = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  index: string,
  value: string
): BaseRule[] => {
  return items.map((item) =>
    item.id === ruleItemId
      ? {
          ...item,
          values: { ...item.values, [index]: value },
        }
      : item
  );
};

export const applyValueRemove = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"],
  index: string
): BaseRule[] => {
  return items.map((item) => {
    if (item.id !== ruleItemId) {
      return item;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [index]: _, ...remainingValues } = item.values;
    return { ...item, values: remainingValues };
  });
};

export const applyRemoveRuleItem = (
  items: BaseRule[],
  ruleItemId: BaseRule["id"]
): BaseRule[] => {
  return items.filter((item) => item.id !== ruleItemId);
};
